/**
 * DV-PROJ-001: Create Setting Modal Component
 *
 * Modal for creating new setting profiles with form validation
 */

import React, { useState } from 'react';
import { X, MapPin, AlertCircle } from 'lucide-react';
import { useCreateSettingProfileMutation } from '@/hooks/useProjectsQuery';
import type { Project, CreateSettingProfileRequest } from '@/types/projects';

interface CreateSettingModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: Project;
}

interface FormErrors {
  name?: string;
  notes?: string;
}

const CreateSettingModal: React.FC<CreateSettingModalProps> = ({
  isOpen,
  onClose,
  project
}) => {
  const [formData, setFormData] = useState<CreateSettingProfileRequest>({
    name: '',
    notes: '',
    referenceImageUrls: [],
    isDefault: false
  });
  const [errors, setErrors] = useState<FormErrors>({});

  const createSettingMutation = useCreateSettingProfileMutation();

  // Reset form when modal opens/closes
  React.useEffect(() => {
    if (!isOpen) {
      setFormData({ name: '', notes: '', referenceImageUrls: [], isDefault: false });
      setErrors({});
    }
  }, [isOpen]);

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Name is required and must be reasonable length
    if (!formData.name.trim()) {
      newErrors.name = 'Setting name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Setting name must be at least 2 characters';
    } else if (formData.name.trim().length > 100) {
      newErrors.name = 'Setting name must be less than 100 characters';
    }

    // Notes are optional but have length limit
    if (formData.notes && formData.notes.length > 500) {
      newErrors.notes = 'Notes must be less than 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await createSettingMutation.mutateAsync({
        projectId: project.id,
        data: {
          name: formData.name.trim(),
          notes: formData.notes?.trim() || undefined,
          referenceImageUrls: formData.referenceImageUrls,
          isDefault: formData.isDefault
        }
      });

      onClose();
    } catch (error) {
      console.error('Failed to create setting:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-black/90 backdrop-blur-xl border border-purple-500/20 rounded-lg shadow-2xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500/20 to-blue-500/20 rounded-lg flex items-center justify-center">
              <MapPin className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h2 className="text-lg font-light text-white">New Setting</h2>
              <p className="text-xs text-white/50 font-mono">Create setting profile</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-white/60 hover:text-white/80 hover:bg-white/10 rounded transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            {/* Setting Name */}
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">
                Setting Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter setting name"
                className={`w-full px-3 py-2 bg-black/50 border rounded-lg text-white/90 placeholder-white/40
                           focus:outline-none transition-colors font-mono text-sm ${
                  errors.name
                    ? 'border-red-500/50 focus:border-red-500/70'
                    : 'border-white/20 focus:border-purple-500/50'
                }`}
                maxLength={100}
              />
              {errors.name && (
                <div className="flex items-center gap-2 mt-1 text-red-400 text-xs">
                  <AlertCircle className="w-3 h-3" />
                  {errors.name}
                </div>
              )}
            </div>

            {/* Setting Notes */}
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Optional setting description or notes"
                rows={3}
                className={`w-full px-3 py-2 bg-black/50 border rounded-lg text-white/90 placeholder-white/40
                           focus:outline-none transition-colors font-mono text-sm resize-none ${
                  errors.notes
                    ? 'border-red-500/50 focus:border-red-500/70'
                    : 'border-white/20 focus:border-purple-500/50'
                }`}
                maxLength={500}
              />
              <div className="flex items-center justify-between mt-1">
                {errors.notes ? (
                  <div className="flex items-center gap-2 text-red-400 text-xs">
                    <AlertCircle className="w-3 h-3" />
                    {errors.notes}
                  </div>
                ) : (
                  <div />
                )}
                <div className="text-xs text-white/40 font-mono">
                  {formData.notes?.length || 0}/500
                </div>
              </div>
            </div>

            {/* Default Setting Checkbox */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isDefault"
                checked={formData.isDefault}
                onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                className="w-4 h-4 bg-black/50 border border-white/20 rounded focus:ring-purple-500/50 text-purple-500"
              />
              <label htmlFor="isDefault" className="text-sm text-white/80">
                Set as default setting for this project
              </label>
            </div>

            {/* Error Message */}
            {createSettingMutation.isError && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <div className="flex items-center gap-2 text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  Failed to create setting. Please try again.
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-white/10">
            <button
              type="button"
              onClick={onClose}
              disabled={createSettingMutation.isPending}
              className="px-4 py-2 text-sm border border-white/20 text-white/70 rounded
                         hover:bg-white/10 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createSettingMutation.isPending || !formData.name.trim()}
              className="px-4 py-2 text-sm bg-purple-500/20 border border-purple-500/50 text-purple-400 rounded
                         hover:bg-purple-500/30 transition-colors disabled:opacity-50
                         flex items-center gap-2"
            >
              {createSettingMutation.isPending ? (
                <>
                  <div className="w-4 h-4 border-2 border-purple-400/30 border-t-purple-400 rounded-full animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <MapPin className="w-4 h-4" />
                  Create Setting
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateSettingModal;